import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip
} from '@mui/material';
import {
  Event as EventIcon,
  Public as PublicIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { db } from '../../../firebase/config';
import { collection, query, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import * as L from 'leaflet';

// Fix for Leaflet marker icons in webpack
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
   iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
   iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
   shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

type DataSource = {
   id: string;
   name: string;
   type: string;
   status: 'active' | 'inactive' | 'error';
   lastSync: Timestamp;
   eventCount: number;
};

type Job = {
   id: string;
   name: string;
   status: 'completed' | 'failed' | 'running';
   createdAt: Timestamp;
   completedAt?: Timestamp;
   source: string;
   eventsProcessed?: number;
};

type EventLocation = {
   id: string;
   title: string;
   lat: number;
   lng: number;
   count: number;
};

const EventDashboard: React.FC = () => {
   const [loading, setLoading] = useState(true);
   const [stats, setStats] = useState({
      totalEvents: 0,
      eventsToday: 0,
      activeSources: 0,
      totalSources: 0,
      scheduledJobs: 0,
      completedJobs: 0,
      failedJobs: 0,
      aiSearches: 0,
   });
   const [recentJobs, setRecentJobs] = useState<Job[]>([]);
   const [dataSources, setDataSources] = useState<DataSource[]>([]);
   const [eventLocations, setEventLocations] = useState<EventLocation[]>([]);

   useEffect(() => {
      const fetchDashboardData = async () => {
         try {
            setLoading(true);

            // This is mock data for demonstration
            // In a real implementation, you would fetch this from Firestore

            // Mock stats
            setStats({
               totalEvents: 2458,
               eventsToday: 37,
               activeSources: 4,
               totalSources: 5,
               scheduledJobs: 8,
               completedJobs: 156,
               failedJobs: 3,
               aiSearches: 24,
            });

            // Mock recent jobs
            setRecentJobs([
               {
                  id: 'job1',
                  name: 'Eventbrite - Daily Sync',
                  status: 'completed',
                  createdAt: Timestamp.fromDate(new Date(Date.now() - 3600000)),
                  completedAt: Timestamp.fromDate(new Date()),
                  source: 'Eventbrite',
                  eventsProcessed: 42,
               },
               {
                  id: 'job2',
                  name: 'Meetup - Full Sync',
                  status: 'completed',
                  createdAt: Timestamp.fromDate(new Date(Date.now() - 7200000)),
                  completedAt: Timestamp.fromDate(new Date(Date.now() - 3600000)),
                  source: 'Meetup',
                  eventsProcessed: 128,
               },
               {
                  id: 'job3',
                  name: 'Perplexity AI - Event Search',
                  status: 'running',
                  createdAt: Timestamp.fromDate(new Date()),
                  source: 'Perplexity AI',
               },
            ]);

            // Mock data sources
            setDataSources([
               {
                  id: 'src1',
                  name: 'Eventbrite',
                  type: 'API',
                  status: 'active',
                  lastSync: Timestamp.fromDate(new Date()),
                  eventCount: 845,
               },
               {
                  id: 'src2',
                  name: 'Meetup',
                  type: 'API',
                  status: 'active',
                  lastSync: Timestamp.fromDate(new Date(Date.now() - 3600000)),
                  eventCount: 632,
               },
               {
                  id: 'src3',
                  name: 'Facebook Events',
                  type: 'API',
                  status: 'error',
                  lastSync: Timestamp.fromDate(new Date(Date.now() - 86400000)),
                  eventCount: 450,
               },
               {
                  id: 'src4',
                  name: 'Manual Entries',
                  type: 'Manual',
                  status: 'active',
                  lastSync: Timestamp.fromDate(new Date(Date.now() - 43200000)),
                  eventCount: 32,
               },
            ]);

            // Mock event locations
            setEventLocations([
               { id: 'loc1', title: 'New York', lat: 40.7128, lng: -74.006, count: 423 },
               { id: 'loc2', title: 'Los Angeles', lat: 34.0522, lng: -118.2437, count: 312 },
               { id: 'loc3', title: 'Chicago', lat: 41.8781, lng: -87.6298, count: 187 },
               { id: 'loc4', title: 'Miami', lat: 25.7617, lng: -80.1918, count: 156 },
               { id: 'loc5', title: 'San Francisco', lat: 37.7749, lng: -122.4194, count: 201 },
            ]);

            setLoading(false);
         } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setLoading(false);
         }
      };

      fetchDashboardData();
   }, []);

   const formatDate = (timestamp: Timestamp) => {
      return new Date(timestamp.seconds * 1000).toLocaleString();
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
         </Box>
      );
   }

   return (
      <Box>
         <Typography variant='h5' gutterBottom component='div'>
            Event Administration Dashboard
         </Typography>

         {/* Stats Cards */}
         <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='h6' component='div' color='primary'>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                           <EventIcon sx={{ mr: 1 }} />
                           Events
                        </Box>
                     </Typography>
                     <Typography variant='h3' component='div' sx={{ mt: 2, fontWeight: 'bold' }}>
                        {stats.totalEvents}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        {stats.eventsToday} new today
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='h6' component='div' color='secondary'>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                           <StorageIcon sx={{ mr: 1 }} />
                           Data Sources
                        </Box>
                     </Typography>
                     <Typography variant='h3' component='div' sx={{ mt: 2, fontWeight: 'bold' }}>
                        {stats.activeSources}/{stats.totalSources}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Active sources
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='h6' component='div' sx={{ color: '#4caf50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                           <ScheduleIcon sx={{ mr: 1 }} />
                           Jobs
                        </Box>
                     </Typography>
                     <Typography variant='h3' component='div' sx={{ mt: 2, fontWeight: 'bold' }}>
                        {stats.scheduledJobs}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        {stats.completedJobs} completed, {stats.failedJobs} failed
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='h6' component='div' sx={{ color: '#f57c00' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                           <SearchIcon sx={{ mr: 1 }} />
                           AI Searches
                        </Box>
                     </Typography>
                     <Typography variant='h3' component='div' sx={{ mt: 2, fontWeight: 'bold' }}>
                        {stats.aiSearches}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Perplexity API searches
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
         </Grid>

         {/* Map and Data Sources */}
         <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
               <Paper sx={{ p: 2, height: 400 }}>
                  <Typography variant='h6' gutterBottom>
                     Event Locations
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 320 }}>
                     {/* <MapContainer
                        // center={[39.8283, -98.5795]}
                        // zoom={4}
                        style={{ height: '100%', width: '100%' }}
                     >
                        <TileLayer
                           attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                           url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                        />
                        {eventLocations.map((location) => (
                           <Marker key={location.id} position={[location.lat, location.lng]}>
                              <Popup>
                                 <Typography variant='subtitle2'>{location.title}</Typography>
                                 <Typography variant='body2'>{location.count} events</Typography>
                              </Popup>
                           </Marker>
                        ))}
                     </MapContainer> */}
                  </Box>
               </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
               <Paper sx={{ p: 2, height: 400, overflow: 'auto' }}>
                  <Typography variant='h6' gutterBottom>
                     Data Sources
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                     {dataSources.map((source) => (
                        <ListItem key={source.id} divider>
                           <ListItemAvatar>
                              <Avatar
                                 sx={{
                                    bgcolor:
                                       source.status === 'active'
                                          ? 'success.main'
                                          : source.status === 'error'
                                            ? 'error.main'
                                            : 'grey.500',
                                 }}
                              >
                                 <StorageIcon />
                              </Avatar>
                           </ListItemAvatar>
                           <ListItemText
                              primary={source.name}
                              secondary={
                                 <>
                                    <Typography component='span' variant='body2'>
                                       {source.eventCount} events • Last sync:{' '}
                                       {formatDate(source.lastSync)}
                                    </Typography>
                                    <br />
                                    <Chip
                                       size='small'
                                       icon={
                                          source.status === 'active' ? (
                                             <CheckIcon />
                                          ) : (
                                             <WarningIcon />
                                          )
                                       }
                                       label={source.status}
                                       color={
                                          source.status === 'active'
                                             ? 'success'
                                             : source.status === 'error'
                                               ? 'error'
                                               : 'default'
                                       }
                                       sx={{ mt: 0.5 }}
                                    />
                                 </>
                              }
                           />
                        </ListItem>
                     ))}
                  </List>
               </Paper>
            </Grid>
         </Grid>

         {/* Recent Jobs */}
         <Grid container spacing={3}>
            <Grid item xs={12}>
               <Paper sx={{ p: 2 }}>
                  <Typography variant='h6' gutterBottom>
                     Recent Jobs
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                     {recentJobs.map((job) => (
                        <ListItem key={job.id} divider>
                           <ListItemAvatar>
                              <Avatar
                                 sx={{
                                    bgcolor:
                                       job.status === 'completed'
                                          ? 'success.main'
                                          : job.status === 'failed'
                                            ? 'error.main'
                                            : 'warning.main',
                                 }}
                              >
                                 <ScheduleIcon />
                              </Avatar>
                           </ListItemAvatar>
                           <ListItemText
                              primary={job.name}
                              secondary={
                                 <>
                                    <Typography component='span' variant='body2'>
                                       Source: {job.source} • Created: {formatDate(job.createdAt)}
                                       {job.completedAt &&
                                          ` • Completed: ${formatDate(job.completedAt)}`}
                                    </Typography>
                                    <br />
                                    <Chip
                                       size='small'
                                       label={job.status}
                                       color={
                                          job.status === 'completed'
                                             ? 'success'
                                             : job.status === 'failed'
                                               ? 'error'
                                               : 'warning'
                                       }
                                       sx={{ mt: 0.5, mr: 1 }}
                                    />
                                    {job.eventsProcessed && (
                                       <Chip
                                          size='small'
                                          label={`${job.eventsProcessed} events processed`}
                                          color='primary'
                                          sx={{ mt: 0.5 }}
                                       />
                                    )}
                                 </>
                              }
                           />
                        </ListItem>
                     ))}
                  </List>
               </Paper>
            </Grid>
         </Grid>
      </Box>
   );
};

export default EventDashboard;
